import { useState, type FormEvent } from "react";
import { Icon } from "../../components/icons";
import { Alert, Button, Card, EmptyState, Input, Mono, Table, TableSkeleton, Td, Th, Tag, cx } from "../../components/ui";
import { formatRelative } from "../../lib/format";
import { money } from "../../lib/money";
import { SelectionBar } from "../../components/SelectionBar";
import { useAddDomainTld, useBulkUpdateDomainTlds, useDomainTlds, useEnableSellableTlds, useSyncDomainTlds, useUpdateDomainTld } from "../../lib/queries";
import type { DomainTld } from "../../lib/types";
import { useDomainsTab } from "./DomainsPage";

/** Below this the amber shows: a margin under the 5.5% payment fee is a sale at a loss. */
const THIN_MARGIN_BPS = 550;

function marginBps(cost: number | null, sell: number | null): number | null {
  if (cost === null || sell === null || cost === 0) return null;
  return Math.round(((sell - cost) / cost) * 10_000);
}

/**
 * What is on the shelf and at what margin.
 *
 * Cost is on this screen and nowhere a customer can see. The sell price is
 * derived from it at read time, so a registry price rise reaches customers
 * on the nightly sync without anyone editing a row; the margin column is
 * what an operator watches. Sellable rows sort first, because they are the
 * ones a switch does anything to.
 */
export function CatalogueTab() {
  const { canWrite } = useDomainsTab();
  const tlds = useDomainTlds();
  const sync = useSyncDomainTlds();
  const enableAll = useEnableSellableTlds();
  const add = useAddDomainTld();
  const [extension, setExtension] = useState("");
  const [filter, setFilter] = useState("");

  /*
   * Selection by extension, for the change that is one act on many rows:
   * "these on sale", "these at 12%". The bar offers exactly those, plus
   * returning a markup to the default; anything else stays per row.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [markupDraft, setMarkupDraft] = useState("");
  const [outcome, setOutcome] = useState<string | null>(null);
  const bulk = useBulkUpdateDomainTlds();

  const all = [...(tlds.data?.data ?? [])].sort((a, b) => Number(b.sellable) - Number(a.sellable) || a.extension.localeCompare(b.extension));
  const needle = filter.trim().toLowerCase().replace(/^\./, "");
  const rows = needle ? all.filter((row) => row.extension.includes(needle)) : all;
  const allShown = rows.length > 0 && rows.every((row) => selected.has(row.extension));
  const toggle = (ext: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(ext)) next.delete(ext);
      else next.add(ext);
      return next;
    });
  const applyBulk = (input: { enabled?: boolean; markupBps?: number | null }, said: string) =>
    bulk.mutate({ extensions: [...selected], ...input }, {
      onSuccess: (result) => {
        setOutcome(`${said} on ${result.updated} extension${result.updated === 1 ? "" : "s"}.`);
        setSelected(new Set());
        setMarkupDraft("");
      },
    });
  const markupBps = markupDraft.trim() === "" ? null : Math.round(Number(markupDraft) * 100);
  const markupValid = markupDraft.trim() !== "" && Number.isFinite(markupBps) && (markupBps as number) >= 0;
  const configured = tlds.data?.registrarConfigured ?? true;
  const defaultBps = tlds.data?.defaultMarkupBps ?? 0;
  const lastSynced = rows.reduce<string | null>((latest, row) => (row.syncedAt && (!latest || row.syncedAt > latest) ? row.syncedAt : latest), null);
  const offButSellable = rows.filter((row) => !row.enabled && row.unsellableReason === null && row.cost.registerMicros !== null && row.restrictions.length === 0).length;

  const onAdd = (event: FormEvent) => {
    event.preventDefault();
    const value = extension.trim().toLowerCase().replace(/^\./, "");
    if (value) add.mutate(value, { onSuccess: () => setExtension("") });
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
          <p className="text-[13px] text-fg-muted">
            Customer pays <span className="text-fg">{(defaultBps / 100).toFixed(0)}%</span> over the registrar's price unless a row says otherwise.
            {lastSynced ? ` Prices synced ${formatRelative(lastSynced)}.` : " Never synced."}
          </p>
          {canWrite && (
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              {offButSellable > 0 && (
                <Button size="sm" variant="outline" loading={enableAll.isPending} onClick={() => enableAll.mutate()}>
                  Put {offButSellable} sellable on sale
                </Button>
              )}
              <Button size="sm" variant="outline" loading={sync.isPending} onClick={() => sync.mutate()} disabled={!configured}>
                Sync prices now
              </Button>
            </div>
          )}
        </div>

        {tlds.error && <Alert>{tlds.error.message}</Alert>}
        {sync.error && <Alert>{sync.error.message}</Alert>}
        {enableAll.error && <Alert>{enableAll.error.message}</Alert>}
        {bulk.error && <Alert>{bulk.error.message}</Alert>}
        {outcome && <Alert tone="success">{outcome}</Alert>}
        {all.length > 8 && (
          <div className="border-b border-line px-5 py-3">
            <div className="w-full sm:w-64">
              <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter extensions" aria-label="Filter extensions" />
            </div>
          </div>
        )}
        {sync.data && (
          <Alert tone="info">
            Synced {sync.data.data.scanned} extension{sync.data.data.scanned === 1 ? "" : "s"}
            {sync.data.data.repriced.length > 0 && ` — repriced .${sync.data.data.repriced.join(", .")}`}
            {sync.data.data.unsellable.length > 0 && ` — ${sync.data.data.unsellable.length} cannot be sold`}
          </Alert>
        )}

        {tlds.isPending ? (
          <TableSkeleton columns={6} />
        ) : rows.length === 0 ? (
          <EmptyState title="Nothing in the catalogue" description="Sync prices and the curated extensions appear, priced; then switch on the ones to sell." />
        ) : (
          <Table
            head={
              <>
                {canWrite && (
                  <Th className="w-8">
                    <input
                      type="checkbox"
                      aria-label="Select every extension shown"
                      checked={allShown}
                      onChange={() =>
                        setSelected((current) => {
                          const next = new Set(current);
                          if (allShown) for (const row of rows) next.delete(row.extension);
                          else for (const row of rows) next.add(row.extension);
                          return next;
                        })
                      }
                      className="h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </Th>
                )}
                <Th>Extension</Th>
                <Th className="whitespace-nowrap">On sale</Th>
                <Th priority="sm">Register / yr</Th>
                <Th priority="md">Renew / yr</Th>
                <Th priority="lg">Transfer</Th>
                <Th priority="md">Markup</Th>
              </>
            }
          >
            {rows.map((tld) => (
              <TldRow
                key={tld.extension}
                tld={tld}
                canWrite={canWrite}
                defaultBps={defaultBps}
                selected={selected.has(tld.extension)}
                onSelect={() => toggle(tld.extension)}
              />
            ))}
          </Table>
        )}
      </Card>

      {canWrite && (
        <SelectionBar
          count={selected.size}
          noun="extension"
          onClear={() => setSelected(new Set())}
          action={
            <>
              <span className="flex items-center gap-1">
                <Input
                  value={markupDraft}
                  onChange={(e) => setMarkupDraft(e.target.value)}
                  placeholder="markup %"
                  inputMode="decimal"
                  aria-label="Markup percent for the selection"
                  className="h-8 w-24 text-xs"
                />
                <Button size="sm" variant="secondary" disabled={!markupValid} loading={bulk.isPending} onClick={() => applyBulk({ markupBps }, `Markup set to ${markupDraft}%`)}>
                  Set markup
                </Button>
              </span>
              <Button size="sm" variant="ghost" loading={bulk.isPending} onClick={() => applyBulk({ markupBps: null }, "Markup returned to the default")}>
                Default markup
              </Button>
              <Button size="sm" variant="secondary" loading={bulk.isPending} onClick={() => applyBulk({ enabled: false }, "Taken off sale")}>
                Off sale
              </Button>
              <Button size="sm" loading={bulk.isPending} onClick={() => applyBulk({ enabled: true }, "Put on sale")}>
                On sale
              </Button>
            </>
          }
        />
      )}

      {canWrite && (
        <form onSubmit={onAdd} className="flex flex-wrap items-center gap-2">
          <div className="w-56">
            <Input value={extension} onChange={(e) => setExtension(e.target.value)} placeholder="Add an extension, e.g. co.uk" />
          </div>
          <Button type="submit" size="sm" variant="outline" loading={add.isPending}>
            Add to catalogue
          </Button>
          <span className="text-[12px] text-fg-subtle">Priced on the next sync. Extensions with registrant paperwork the platform does not collect fail at the registrar.</span>
          {add.error && <span className="text-[12px] text-danger">{add.error.message}</span>}
        </form>
      )}
    </div>
  );
}

function OnSaleSwitch({ on, disabled, onChange }: { on: boolean; disabled: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className="-m-2 rounded-md p-2 focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className={cx("flex h-5 w-9 items-center rounded-full border transition-colors duration-200", on ? "border-accent bg-accent" : "border-line-strong bg-raised")}>
        <span className={cx("size-3.5 rounded-full transition-transform duration-200", on ? "translate-x-[18px] bg-white" : "translate-x-[2px] bg-fg-subtle")} />
      </span>
    </button>
  );
}

function PriceCell({ cost, sell }: { cost: number | null; sell: number | null }) {
  if (cost === null) return <span className="text-fg-subtle">—</span>;
  const margin = marginBps(cost, sell);
  return (
    <>
      <div className="text-fg">{sell === null ? "—" : money(sell)}</div>
      <div className={cx("text-[11px] whitespace-nowrap", margin !== null && margin < THIN_MARGIN_BPS ? "text-warn" : "text-fg-subtle")}>
        cost {money(cost)}
        {margin !== null && ` · ${(margin / 100).toFixed(1)}%`}
      </div>
    </>
  );
}

function TldRow({
  tld,
  canWrite,
  defaultBps,
  selected,
  onSelect,
}: {
  tld: DomainTld;
  canWrite: boolean;
  defaultBps: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const update = useUpdateDomainTld();
  const [draft, setDraft] = useState<string | null>(null);
  const busy = update.isPending && update.variables?.extension === tld.extension;
  const priced = tld.cost.registerMicros !== null;

  const commitMarkup = () => {
    if (draft === null) return;
    const trimmed = draft.trim();
    const bps = trimmed === "" ? null : Math.round(Number(trimmed) * 100);
    if (trimmed !== "" && (!Number.isFinite(bps) || (bps as number) < 0)) return;
    if (bps !== tld.markupBps) update.mutate({ extension: tld.extension, input: { markupBps: bps } });
    setDraft(null);
  };

  return (
    <tr className={cx(!priced && "opacity-60", selected && "bg-raised/40")}>
      {canWrite && (
        <Td>
          <input
            type="checkbox"
            aria-label={`Select .${tld.extension}`}
            checked={selected}
            onChange={onSelect}
            className="h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
          />
        </Td>
      )}
      <Td>
        <span className="inline-flex items-center gap-1.5">
          <Mono className="text-fg">.{tld.extension}</Mono>
          {tld.restrictions.length > 0 && <RegistryNotes notes={tld.restrictions} extension={tld.extension} />}
        </span>
        {tld.unsellableReason && <div className="mt-0.5 max-w-xs text-[11px] text-danger">{tld.unsellableReason}</div>}
        {priced && !tld.privateWhoisAllowed && <div className="mt-0.5 text-[11px] text-fg-subtle">no WHOIS privacy</div>}
        {priced && (tld.minPeriod > 1 || tld.maxPeriod < 10) && (
          <div className="mt-0.5 text-[11px] text-fg-subtle">
            {tld.minPeriod}–{tld.maxPeriod} years
          </div>
        )}
      </Td>
      <Td>
        <div className="flex items-center gap-2">
          {canWrite ? (
            <OnSaleSwitch on={tld.enabled} disabled={busy || tld.unsellableReason !== null || !priced} onChange={(next) => update.mutate({ extension: tld.extension, input: { enabled: next } })} />
          ) : (
            <Tag tone={tld.sellable ? "good" : "neutral"}>{tld.enabled ? "on" : "off"}</Tag>
          )}
          {tld.enabled && !tld.sellable && <Tag tone="bad">cannot sell</Tag>}
        </div>
      </Td>
      <Td priority="sm">
        <PriceCell cost={tld.cost.registerMicros} sell={tld.sell.registerMicros} />
      </Td>
      <Td priority="md">
        <PriceCell cost={tld.cost.renewMicros} sell={tld.sell.renewMicros} />
      </Td>
      <Td priority="lg">
        <PriceCell cost={tld.cost.transferMicros} sell={tld.sell.transferMicros} />
      </Td>
      <Td priority="md">
        {canWrite ? (
          <div className="flex items-center gap-1">
            <Input
              value={draft ?? (tld.markupBps === null ? "" : (tld.markupBps / 100).toString())}
              placeholder={(defaultBps / 100).toString()}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitMarkup}
              onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
              className="w-16 text-right"
              inputMode="decimal"
              disabled={busy}
              aria-label={`Markup for .${tld.extension}`}
            />
            <span className="text-[12px] text-fg-subtle">%</span>
            {tld.markupBps === null && <span className="text-[11px] text-fg-subtle">default</span>}
          </div>
        ) : (
          <span>{(tld.effectiveMarkupBps / 100).toFixed(1)}%</span>
        )}
      </Td>
    </tr>
  );
}

/**
 * The registrar's notes on an extension, behind an "i".
 *
 * They arrive as a paragraph -- .dev's is four sentences about HSTS with two
 * links -- and printed under the extension they pushed a one-line row to
 * five and made the whole shelf read as a page of warnings. Most are not
 * warnings; they are things to know. So: an icon in the row, the text on
 * hover or focus, and the amber tone kept for the icon alone. Kept off the
 * bulk "put on sale" all the same: a note is a reason for a person to read
 * before switching on, whatever it turns out to say.
 */
function RegistryNotes({ notes, extension }: { notes: string[]; extension: string }) {
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label={`What the registry says about .${extension}`}
        className="inline-flex size-4 items-center justify-center rounded-full text-warn transition-colors hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
      >
        <Icon name="info" className="size-3.5" />
      </button>
      <span
        role="tooltip"
        className="invisible absolute top-full left-0 z-20 mt-1.5 w-80 rounded-md border border-line bg-surface p-3 text-left text-[12px] leading-5 whitespace-normal text-fg-muted opacity-0 shadow-lg transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
      >
        <span className="micro mb-1 block text-fg-subtle">registry notes</span>
        {notes.map((note, i) => (
          <span key={i} className={cx("block", i > 0 && "mt-2")}>
            {note}
          </span>
        ))}
      </span>
    </span>
  );
}
